import LsAdminPageSectionHeader from 'admin/loadSchedulingAdmin/LsAdminPageSectionHeader'
import { orderBy } from 'lodash'
import { DateTime } from 'luxon'
import { useEffect, useState, useContext } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from 'react-query'
import IWGenericTable from 'shared/components/thunderbolt/IWGenericTable'
import IWLoading from 'shared/components/thunderbolt/IWLoading'
import LayoutContext from 'shared/contexts/LayoutContext'
import { callWithTryCatch, convertToCVS, downloadContent } from 'shared/helpers'
import {
  ClearedBidDTO,
  getClientClearedBids,
} from 'shared/loadSchedulingClient'
import { lsClearedBidsTableSchema } from '../schemas'

type TableItem = {
  date: string
  market: string
  brand: string
  zone: string
  location: string
  locationName: string
  submitType: string
  hour: number
  load: number
  price: number
}

const USE_QUERY_KEY = 'ls-cleared-bids'

const tableColumns = lsClearedBidsTableSchema

export default function AwardedBids() {
  const { t } = useTranslation()
  const { timezone } = useContext(LayoutContext)
  const [items, setItems] = useState<TableItem[]>([])
  const [filteredItems, setFilteredItems] = useState<TableItem[]>([])

  const { data: clearedBids, isLoading } = useQuery({
    queryKey: [USE_QUERY_KEY],
    queryFn: async () =>
      callWithTryCatch({
        handler: async () => {
          const startDate = DateTime.local()
            .setZone(timezone)
            .minus({ months: 1 })
            .toISODate()
          const endDate = DateTime.local()
            .setZone(timezone)
            .endOf('month')
            .toISODate()

          return getClientClearedBids({
            startDate: startDate!,
            endDate: endDate!,
          })
        },
        onError: (error) => {
          console.error('Error fetching cleared bids', error)
          return []
        },
      }),
  })

  const tableMapper = (awards: ClearedBidDTO[]): TableItem[] => {
    const bids = awards?.reduce((memo, current) => {
      const hourly = current.clearedBids.map((element) => ({
        date: current.date,
        market: current.market,
        brand: current.brand,
        zone: current.zone,
        location: current.location,
        locationName: current.locationName,
        submitType: current.submitType,
        hour: element.interval,
        load: Math.abs(element.load),
        price: element.price,
      }))

      memo.push(...hourly)
      return memo
    }, [] as TableItem[])

    return orderBy(bids, ['date'], ['desc']) as TableItem[]
  }

  const handleDownload = () => {
    const csv = convertToCVS(filteredItems)

    downloadContent({
      filename: `cleared-and-awards-${DateTime.local()
        .setZone(timezone)
        .toMillis()}`,
      ext: 'csv',
      content: csv,
    })
  }

  useEffect(() => {
    if (clearedBids?.length) {
      setItems(tableMapper(clearedBids))
    }
  }, [clearedBids])

  return (
    <main>
      <LsAdminPageSectionHeader
        header={t('loadScheduling.titles.awardedBids')}
        desc={t('loadScheduling.descriptions.awardedBids')}
      >
        {isLoading && <IWLoading />}

        {Boolean(items?.length) && (
          <IWGenericTable
            border
            isLoading={isLoading}
            items={items}
            columns={tableColumns}
            pinnedColumns={{ date: 'left' }}
            showColumnSettings
            onDownload={handleDownload}
            onTableItemsUpdated={(updatedItems) => {
              setFilteredItems(updatedItems)
            }}
          />
        )}
      </LsAdminPageSectionHeader>
    </main>
  )
}
